// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";
import { Button, Dialog, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { OpenItemRows, ownerLabel } from "./AskV2OpenItemRow";
import { AskV2OpenItemsGroups } from "./AskV2OpenItemsGroups";
import { AskV2OpenItemsSplit } from "./AskV2OpenItemsSplit";
import { AskV2ProjectDoneBadge } from "./AskV2ProjectDoneBadge";
import { openItemKindLabel } from "./openItemKindLabel";
import { openItemShortNeeded } from "./openItemRowTitle";
import {
  type UseProjectOpenItemsOptions,
  useProjectOpenItems,
} from "./useProjectOpenItems";

import { ASK_V2_RAIL_SECTION_CLASS } from "../rail/AskV2RailSection";
type Member = {
  _id: string;
  name?: string | null;
  email?: string | null;
};

const MARK_DONE_WARNING_MAX_ROWS = 5;

function MarkDoneWarning({
  open,
  items,
  members,
  pending,
  onCancel,
  onOverride,
}: {
  open: boolean;
  items: AskPunttOpenItem[];
  members: Member[];
  pending: boolean;
  onCancel: () => void;
  onOverride: () => void;
}) {
  const { t } = useTranslation("ui");
  const visible = items.slice(0, MARK_DONE_WARNING_MAX_ROWS);
  const remainder = items.length - visible.length;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <Dialog.Content data-testid="ask-v2-mark-project-done-warning">
        <Dialog.Title>
          {t("askV2.openItems.warningTitle", { count: items.length })}
        </Dialog.Title>
        <Dialog.Description>
          {t("askV2.openItems.warningDescription")}
        </Dialog.Description>
        <ul className="my-3 flex flex-col gap-2">
          {visible.map((item) => {
            const kindLabel = openItemKindLabel(item.kind, t);
            const needed = openItemShortNeeded(item);
            const owner = ownerLabel(item, members);
            return (
              <li
                key={item.id}
                data-testid={`ask-v2-open-done-item-${item.id}`}
                aria-label={`${kindLabel}: ${needed}, ${owner}`}
                className="list-none text-[13px] text-puntt-neutral-gray-12"
              >
                <span className="block text-[11px] font-medium uppercase tracking-wide text-puntt-neutral-gray-10">
                  {kindLabel}
                </span>
                <span className="block leading-snug">{needed}</span>
                {owner.length > 0 ? (
                  <span className="block text-[12px] leading-snug text-puntt-neutral-gray-10">
                    {owner}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
        {remainder > 0 ? (
          <Text
            size="2"
            data-testid="ask-v2-open-done-more"
            className="mb-3 text-puntt-neutral-gray-11"
          >
            {t("askV2.openItems.warningMore", { count: remainder })}
          </Text>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="soft"
            color="gray"
            data-testid="ask-v2-mark-project-done-cancel"
            onClick={onCancel}
          >
            {t("askV2.openItems.cancel")}
          </Button>
          <Button
            type="button"
            data-testid="ask-v2-mark-project-done-override"
            disabled={pending}
            onClick={onOverride}
          >
            {t("askV2.openItems.override")}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function AskV2OpenItems({
  projectId,
  members,
  onAttachOpenItem,
  reviewHasRun = false,
  projectOpenItemsOptions,
  currentUserId,
  projectOwnerId = "",
  projectOwnerName = "",
  railHost,
  splitHost,
  onOpenSplit,
  onCloseSplit,
}: {
  projectId: string;
  members: Member[];
  onAttachOpenItem: (item: AskPunttOpenItem) => void;
  reviewHasRun?: boolean;
  projectOpenItemsOptions?: UseProjectOpenItemsOptions;
  currentUserId: string | undefined;
  projectOwnerId?: string;
  projectOwnerName?: string;
  railHost: HTMLElement | null;
  splitHost: HTMLElement | null;
  onOpenSplit: () => void;
  onCloseSplit: () => void;
}) {
  const { t } = useTranslation("ui");
  const { query, mark, assign, nudge } = useProjectOpenItems(
    projectId,
    projectOpenItemsOptions,
  );
  const openItems = (query.data?.openItems ?? []).filter(
    (item) => item.status !== "cleared",
  );
  useEffect(() => {
    if (!query.isSuccess) return;
    if (splitHost != null && openItems.length === 0) onCloseSplit();
  }, [query.isSuccess, splitHost, openItems.length, onCloseSplit]);
  const [nudgePendingItemId, setNudgePendingItemId] = useState<string | null>(
    null,
  );
  const [nudgedItemIds, setNudgedItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [handedItemIds, setHandedItemIds] = useState<Set<string>>(
    () => new Set(),
  );
  const canMarkProjectDone = query.data?.canMarkProjectDone === true;
  const mayAssignOpenItems = query.data?.mayAssignOpenItems === true;
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningList, setWarningList] = useState<AskPunttOpenItem[]>([]);

  if (!query.isSuccess) return null;

  const items = openItems;
  const doneAt = query.data.doneAt;
  const doneBadge = (
    <AskV2ProjectDoneBadge doneAt={doneAt} testId="ask-v2-project-done-badge" />
  );

  // why: before a review finishes, an empty rollup is not "no open items" —
  // the list has not been produced yet. Hide the section until then.
  if (items.length === 0 && !warningOpen && !reviewHasRun) {
    // why: returning null here drops the done badge once every row is cleared.
    // Portal only: this component sits outside the sidebar, and an inline
    // badge would render above the chat.
    return railHost != null ? createPortal(doneBadge, railHost) : null;
  }

  async function onMark() {
    setWarningList(items);
    setWarningOpen(true);
  }

  async function onOverride() {
    await mark.mutateAsync(true);
    setWarningOpen(false);
  }

  // why: the count button is the control — a section toggle repeats "Open items" and hides the only action.
  const railSection =
    items.length > 0 || reviewHasRun ? (
      <div
        className={`${ASK_V2_RAIL_SECTION_CLASS} px-4 py-3`}
        data-testid="ask-v2-open-items-rail"
      >
        {items.length === 0 ? (
          <>
            {doneBadge}
            <Text size="2" data-testid="ask-v2-open-items-empty">
              {t("askV2.openItems.empty")}
            </Text>
          </>
        ) : (
          <div className="flex flex-col gap-3">
            {doneAt != null ? doneBadge : null}
            <Button
              type="button"
              variant="soft"
              className="w-full"
              data-testid="ask-v2-open-items-cta"
              onClick={onOpenSplit}
            >
              {t("askV2.openItems.listName")}{" "}
              <span data-testid="ask-v2-open-items-cta-count">
                {items.length}
              </span>
            </Button>
          </div>
        )}
      </div>
    ) : null;

  return (
    <>
      {railHost != null && railSection != null
        ? createPortal(railSection, railHost)
        : null}
      {splitHost != null && items.length > 0
        ? createPortal(
            <AskV2OpenItemsSplit onClose={onCloseSplit}>
              {doneAt != null ? doneBadge : null}
              {doneAt == null && canMarkProjectDone ? (
                <Button
                  type="button"
                  variant="soft"
                  data-testid="ask-v2-mark-project-done"
                  aria-label={t("askV2.openItems.markDone")}
                  disabled={mark.isPending}
                  onClick={() => {
                    void onMark();
                  }}
                  className="mb-3"
                >
                  {t("askV2.openItems.markDone")}
                </Button>
              ) : null}
              <AskV2OpenItemsGroups
                items={items}
                currentUserId={currentUserId}
                renderRows={(rows) => (
                  <OpenItemRows
                    projectId={projectId}
                    items={rows}
                    members={members}
                    mayAssign={mayAssignOpenItems}
                    assignPending={assign.isPending}
                    handedItemIds={handedItemIds}
                    projectOwnerId={projectOwnerId}
                    projectOwnerName={projectOwnerName}
                    currentUserId={currentUserId}
                    nudgePendingItemId={nudgePendingItemId}
                    nudgedItemIds={nudgedItemIds}
                    onHandToOwner={(itemId) => {
                      if (projectOwnerId.length === 0) return;
                      void assign
                        .mutateAsync({
                          itemId,
                          ownerUserId: projectOwnerId,
                        })
                        .then(() => {
                          setHandedItemIds((current) => {
                            const next = new Set(current);
                            next.add(itemId);
                            return next;
                          });
                        });
                    }}
                    onAssign={(itemId, ownerUserId) => {
                      setHandedItemIds((current) => {
                        const next = new Set(current);
                        next.delete(itemId);
                        return next;
                      });
                      void assign.mutateAsync({ itemId, ownerUserId });
                    }}
                    onNudge={(itemId) => {
                      setNudgePendingItemId(itemId);
                      void nudge
                        .mutateAsync(itemId)
                        .then(() => {
                          setNudgedItemIds((current) => {
                            const next = new Set(current);
                            next.add(itemId);
                            return next;
                          });
                        })
                        .finally(() => {
                          setNudgePendingItemId((current) =>
                            current === itemId ? null : current,
                          );
                        });
                    }}
                    onAttach={onAttachOpenItem}
                  />
                )}
              />
            </AskV2OpenItemsSplit>,
            splitHost,
          )
        : null}
      <MarkDoneWarning
        open={warningOpen}
        items={warningList}
        members={members}
        pending={mark.isPending}
        onCancel={() => setWarningOpen(false)}
        onOverride={() => {
          void onOverride();
        }}
      />
    </>
  );
}
